import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { searchCoverageHours } from 'graphql-queries';

import {
  COVERAGE_HOURS_PAIR_WINDOW,
  CoverageHoursPair,
  CoverageHoursPairTarget,
  advancePairLoad,
  startPairLoad,
} from './coverageHoursPairLoad';
import {
  SearchCoverageHoursResponse,
  toCoverageHoursEntries,
  toCoverageHoursPageInfo,
} from './useCoverageHours';

export class CoverageHoursPairLoadError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CoverageHoursPairLoadError';
  }
}

export type UseCoverageHoursPairResult = {
  pair: CoverageHoursPair | null;
  loading: boolean;
  error?: CoverageHoursPairLoadError;
  loadPair: (target: CoverageHoursPairTarget) => Promise<void>;
  clearPair: () => void;
};

export function useCoverageHoursPair(
  channelId: string
): UseCoverageHoursPairResult {
  const [pair, setPair] = useState<CoverageHoursPair | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CoverageHoursPairLoadError | undefined>();

  const [executeSearch] = useLazyQuery<SearchCoverageHoursResponse>(
    searchCoverageHours,
    { fetchPolicy: 'network-only' }
  );

  const clearPair = useCallback(() => {
    setPair(null);
    setError(undefined);
  }, []);

  const reportFailure = useCallback((cause?: unknown) => {
    setError(
      new CoverageHoursPairLoadError('Could not load coverage hours', { cause })
    );
    setLoading(false);
  }, []);

  const loadPair = useCallback(
    async (target: CoverageHoursPairTarget) => {
      clearPair();
      setLoading(true);

      let progress = startPairLoad();
      let start = 0;

      for (;;) {
        const result = await executeSearch({
          variables: {
            channelId,
            pagination: { start, perPage: COVERAGE_HOURS_PAIR_WINDOW },
          },
        });

        const page = result.data?.searchCoverageHours;

        if (!page) {
          reportFailure(result.error);

          return;
        }

        const advanced = advancePairLoad(progress, target, {
          routings: page.coverageHours.map(toCoverageHoursEntries),
          pageInfo: toCoverageHoursPageInfo(page.pageInfo),
        });

        progress = advanced.progress;

        if (advanced.step.kind === 'done') {
          setPair(advanced.step.pair);
          setLoading(false);

          return;
        }

        if (advanced.step.kind === 'failed') {
          reportFailure();

          return;
        }

        start = advanced.step.start;
      }
    },
    [channelId, clearPair, executeSearch, reportFailure]
  );

  return { pair, loading, error, loadPair, clearPair };
}
